import React, { forwardRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useTheme } from '../ThemeContext';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip);

interface ExpenseBreakdownChartProps {
  data: Array<{ category: string; value: number; color: string }>;
  activeEntity?: string;
}

const ExpenseBreakdownChart = forwardRef<any, ExpenseBreakdownChartProps>(({ data, activeEntity }, ref) => {
  const { chart } = useTheme();

  // Map category colors dynamically to brand visual identity under JUARA_GADAI mode
  const mappedData = data.map(item => {
    if (activeEntity === 'JUARA_GADAI') {
      let color = item.color;
      if (item.category === 'Personnel') color = '#303795';      // Navy
      else if (item.category === 'Office & Ops') color = '#F68B1F'; // Orange
      else if (item.category === 'Depresiasi') color = '#4A4FBF';   // Navy Light
      else if (item.category === 'Travel') color = '#FBA94D';        // Orange Light
      else if (item.category === 'Other OpEx') color = '#9598A9';    // Gray-400
      else if (item.category === 'Bunga') color = '#EF4444';         // Red
      else if (item.category === 'Repair Lelang') color = '#22C55E'; // Green
      return { ...item, color };
    }
    return item;
  });

  const totalExpenses = mappedData.reduce((sum, item) => sum + item.value, 0);

  // Formatter for values: formats >= 1000 Juta as M (Milyar), and formats with standard comma desimal
  const formatJt = (val: number): string => {
    const abs = Math.abs(val);
    const prefix = val < 0 ? '-' : '';
    if (abs >= 1e9) {
      const formatted = (abs / 1e9).toFixed(2).replace('.', ',');
      return `${prefix}${formatted} M`;
    }
    const jtVal = abs / 1e6;
    const formatted = jtVal.toFixed(1).replace('.', ',');
    return `${prefix}${formatted} Jt`;
  };

  const formatFullRupiah = (val: number): string => {
    return 'Rp ' + Math.round(val).toLocaleString('id-ID');
  };

  // If there are no expenses, render a clean fallback card
  if (totalExpenses === 0) {
    return (
      <div className="chart-card animate-in" style={{ height: '100%' }}>
        <div className="chart-card-header">
          <div>
            <div className="chart-card-title">🍩 Expense Breakdown</div>
            <div className="chart-card-subtitle">Persentase pengeluaran berdasarkan kategori</div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
          Tidak ada data pengeluaran pada periode terpilih.
        </div>
      </div>
    );
  }

  const chartData = {
    labels: mappedData.map(d => d.category),
    datasets: [
      {
        data: mappedData.map(d => d.value),
        backgroundColor: mappedData.map(d => d.color),
        borderColor: '#ffffff',
        borderWidth: 2.5,
        hoverBorderColor: '#ffffff',
        hoverBorderWidth: 2.5,
        hoverOffset: 3,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '72%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: chart.tooltipBg,
        titleColor: chart.tooltipTitle,
        bodyColor: chart.tooltipBody,
        borderColor: chart.tooltipBorder,
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        titleFont: { family: 'var(--font)', size: 13, weight: '600' },
        bodyFont: { family: 'var(--font)', size: 12 },
        callbacks: {
          label: (ctx: any) => {
            const val = ctx.parsed;
            const pct = ((val / totalExpenses) * 100).toFixed(1).replace('.', ',');
            return ` ${ctx.label}: ${formatFullRupiah(val)} (${pct}%)`;
          },
        },
      },
      datalabels: { display: false },
    },
  };

  return (
    <div className="chart-card animate-in" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="chart-card-header">
        <div>
          <div className="chart-card-title">🍩 Expense Breakdown</div>
          <div className="chart-card-subtitle">Persentase pengeluaran berdasarkan kategori</div>
        </div>
      </div>

      {/* Main Relative Container for Doughnut and outside labels - Height tightened to 240px to align heights */}
      <div style={{ 
        position: 'relative', 
        width: '100%', 
        maxWidth: '350px', 
        height: '240px', 
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1
      }}>
        {/* Doughnut Chart Canvas wrapper */}
        <div className="chart-wrapper donut-chart" style={{ width: '144px', height: '144px', position: 'relative', flexShrink: 0 }}>
          <Doughnut ref={ref} data={chartData} options={options} />
          
          {/* Elegant Center Total text */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '1px',
            pointerEvents: 'none',
            zIndex: 2
          }}>
            <div style={{ fontSize: '8px', color: 'var(--text-muted)', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>TOTAL</div>
            <div style={{ fontSize: '16px', color: '#303795', fontWeight: 800, lineHeight: 1.15 }}>
              {formatJt(totalExpenses)}
            </div>
            <div style={{ fontSize: '8.5px', color: 'var(--text-muted)', fontWeight: 500, whiteSpace: 'nowrap' }}>
              {formatFullRupiah(totalExpenses)}
            </div>
          </div>
        </div>

        {/* MATHEMATICAL OUTSIDE LABELS (Trigonometry-positioned + Circle-spaced to prevent overlaps) */}
        {(() => {
          let currentAngle = -0.5 * Math.PI; // Start at top center (-90 degrees)
          
          // Build list of labels with target angles
          const allLabels = mappedData.map((item) => {
            const pct = item.value / totalExpenses;
            const targetAngle = currentAngle + (pct * Math.PI);
            currentAngle += pct * 2 * Math.PI;
            return {
              item,
              displayAngle: targetAngle,
              pct,
            };
          });

          // Only show labels for categories representing >= 1.0%
          const visibleLabels = allLabels.filter(l => l.pct >= 0.01);

          // Space out angles if they are too close to each other
          const minGap = 0.35; // ~20 degrees
          for (let iter = 0; iter < 12; iter++) {
            for (let i = 0; i < visibleLabels.length; i++) {
              const nextIdx = (i + 1) % visibleLabels.length;
              
              let diff = visibleLabels[nextIdx].displayAngle - visibleLabels[i].displayAngle;
              while (diff < -Math.PI) diff += 2 * Math.PI;
              while (diff > Math.PI) diff -= 2 * Math.PI;
              
              if (Math.abs(diff) < minGap) {
                const overlap = minGap - Math.abs(diff);
                const direction = diff >= 0 ? 1 : -1;
                
                visibleLabels[i].displayAngle -= (overlap / 2) * direction;
                visibleLabels[nextIdx].displayAngle += (overlap / 2) * direction;
              }
            }
          }

          // Spacing dimensions for a perfect 92px circle inside the 350x240 box
          const distanceX = 26.3; // % of half-width (175px)
          const distanceY = 38.3; // % of half-height (120px)

          return visibleLabels.map(({ item, displayAngle }, idx) => {
            const x = 50 + distanceX * Math.cos(displayAngle);
            const y = 50 + distanceY * Math.sin(displayAngle);

            const cosVal = Math.cos(displayAngle);
            const sinVal = Math.sin(displayAngle);
            
            let align = 'center';
            let transform = 'translate(-50%, -50%)';

            if (cosVal > 0.35) {
              align = 'left';
              transform = 'translate(10px, -50%)'; // Offset text to the right
            } else if (cosVal < -0.35) {
              align = 'right';
              transform = 'translate(calc(-100% - 10px), -50%)'; // Offset text to the left
            } else if (sinVal > 0) {
              align = 'center';
              transform = 'translate(-50%, 10px)'; // Offset text below
            } else {
              align = 'center';
              transform = 'translate(-50%, calc(-100% - 10px))'; // Offset text above
            }

            return (
              <div
                key={idx}
                style={{
                  position: 'absolute',
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: transform,
                  textAlign: align as any,
                  fontSize: '10.5px',
                  lineHeight: '1.25',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  zIndex: 3
                }}
              >
                <div style={{ fontWeight: 700, color: item.color }}>
                  {item.category}
                </div>
                <div style={{ fontWeight: 700, color: '#303795', fontSize: '11px', marginTop: '1px' }}>
                  {formatJt(item.value)}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '9px', fontWeight: 600 }}>
                  {((item.value / totalExpenses) * 100).toFixed(1).replace('.', ',')}%
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* Premium Bottom Legend Row */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '8px 12px',
        padding: '10px 10px 10px 10px',
        marginTop: '10px',
        borderTop: '1px solid var(--border-light)',
        flexShrink: 0
      }}>
        {mappedData.map((item, idx) => (
          <div key={idx} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: '10.5px',
            fontWeight: 600,
            color: 'var(--text-secondary)'
          }}>
            <div style={{
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              backgroundColor: item.color,
              flexShrink: 0
            }} />
            <span>{item.category}</span>
            <span style={{ color: 'var(--text-muted)', fontWeight: 700, fontSize: '9.5px' }}>
              {((item.value / totalExpenses) * 100).toFixed(1).replace('.', ',')}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});

ExpenseBreakdownChart.displayName = 'ExpenseBreakdownChart';

export default ExpenseBreakdownChart;
